import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import SubtleBadge from "components/common/SubtleBadge";
import { useAdvanceTable } from "hooks";
import useClientIDs from "hooks/clientIds/useClientIds";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Alert, Dropdown, Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { ClientIDDetailsModal } from "./ClientIDDetailsModal";
import { toast } from "react-toastify";
import { ClientIDUpdateModal } from "./ClientIDUpdateModal";
import useFormattedDate from "hooks/useFormatDate";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";

export const ClientIDs = () => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const {
    clientIDs,
    loading,
    fetchClientIDById,
    selectedID,
    updateClientID,
    deleteClientID,
    error,
  } = useClientIDs();

  const [showModal, setShowModal] = useState(false);

  const handleViewClientID = async (id) => {
    try {
      await fetchClientIDById(id);
      setShowModal(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const handleUpdateClientID = async (id) => {
    try {
      await fetchClientIDById(id);
      setShowUpdateModal(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const formatKey = (key) => `${key.slice(0, 12)}...${key.slice(-6)}`;
  const {formatDateTime} = useFormattedDate();

  const columns = [
    {
      accessorKey: "id",
      header: "Client ID",
      cell: ({ getValue }) => formatKey(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "apiKeyId",
      header: "API Key",
      cell: ({ getValue }) => formatKey(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();

        const statusMap = {
          ACTIVE: { type: "success", label: "Active" },
          NEW: { type: "primary", label: "New" },
          BLOCKED: { type: "danger", label: "Blocked" },
          SUSPENDED: { type: "warning", label: "Suspended" },
          INACTIVE: { type: "secondary", label: "Inactive" },
          RATE_LIMITED: { type: "info", label: "Rate Limited" },
          THROTTLED: { type: "dark", label: "Throttled" },
          DELETED: { type: "danger", label: "Deleted" },
        };

        const { type, label } = statusMap[value];

        const icon = value == "ACTIVE" ? "check" : "ban";
        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      cell: ({ getValue }) =>
        formatDateTime(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleViewClientID(item.id)}>
                View
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleUpdateClientID(item.id)}>
                Edit
              </Dropdown.Item>
              <Dropdown.Divider as="div" />
              <Dropdown.Item onClick={() => {}} className="text-danger">
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: clientIDs,
    columns,
    sortable: true,
    pagination: true,
    selection: true,
    perPage: 20,
  });

  if (loading && clientIDs.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          Error
        </Alert.Heading>
        <p style={{ fontSize: "13.33px" }}>{error}</p>
      </Alert>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Client IDs"
        subtitle="Client IDs from the server for monitoring and management."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
        </div>
      </AdvanceTableProvider>

      {/*Details Modal */}
      <ClientIDDetailsModal
        selectedID={selectedID}
        loading={loading}
        showModal={showModal}
        setShowModal={setShowModal}
      />

      <ClientIDUpdateModal
        selectedID={selectedID}
        loading={loading}
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        updateClientID={updateClientID}
      />
    </>
  );
};
